import React, { useRef, useState } from 'react';
import { Todo } from '../types/Todo';
import { exportTodosToJSON, importTodosFromJSON, saveTodosToLocalFile } from '../services/jsonService';
import ImportDialog from './ImportDialog';

interface DataManagerProps {
  todos: Todo[];
  onImportTodos: (todos: Todo[], mergeMode: boolean) => void;
}

const DataManager: React.FC<DataManagerProps> = ({ todos, onImportTodos }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importDialog, setImportDialog] = useState<{
    isOpen: boolean;
    importedTodos: Todo[];
  }>({
    isOpen: false,
    importedTodos: []
  });

  const handleExport = () => {
    try {
      exportTodosToJSON(todos);
    } catch (error) {
      alert('Failed to export todos. Please try again.');
    }
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const importedTodos = await importTodosFromJSON(file);
      
      // Show import dialog
      setImportDialog({
        isOpen: true,
        importedTodos
      });
      
    } catch (error) {
      alert(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImportConfirm = (mergeMode: boolean) => {
    const { importedTodos } = importDialog;
    
    onImportTodos(importedTodos, mergeMode);
    
    const duplicateCount = mergeMode ? 
      importedTodos.filter(todo => todos.some(existing => existing.id === todo.id)).length : 0;
    
    let message = `✅ Tasks imported successfully!`;
    if (mergeMode && duplicateCount > 0) {
      message += `\n\n${duplicateCount} duplicate tasks were skipped.`;
    }
    
    alert(message);
  };

  const handleBackup = () => {
    try {
      saveTodosToLocalFile(todos);
    } catch (error) {
      alert('Failed to create backup. Please try again.');
    }
  };

  return (
    <>
      <ImportDialog
        isOpen={importDialog.isOpen}
        onClose={() => setImportDialog({ isOpen: false, importedTodos: [] })}
        onImport={handleImportConfirm}
        importedTodos={importDialog.importedTodos}
        existingCount={todos.length}
      />
      
      <div className="card mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">📁 Data Management</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={handleExport}
          className="btn-primary flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export JSON
        </button>

        <button
          onClick={handleImport}
          className="btn-secondary flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
          </svg>
          Import JSON
        </button>

        <button
          onClick={handleBackup}
          className="btn-secondary flex items-center justify-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Create Backup
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">💡 Data Management Tips:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Export:</strong> Download your todos as a JSON file</li>
          <li>• <strong>Import:</strong> Load todos from a previously exported JSON file</li>
          <li>• <strong>Merge Mode:</strong> Imported tasks are added to existing ones (recommended)</li>
          <li>• <strong>Replace Mode:</strong> Imported tasks replace all existing tasks</li>
          <li>• <strong>Backup:</strong> Create a timestamped backup of your current todos</li>
          <li>• Your data is automatically saved to browser storage</li>
        </ul>
      </div>
    </div>
    </>
  );
};

export default DataManager;
